import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import QtyStepper from '../../../components/orizon-ui/QtyStepper';
import {
  TextField, NumberField, SelectField, TextareaField, ToggleField, FormSection, InfoBox,
} from '../../../components/orizon-ui/FormField';
import { useToast } from '../../../components/orizon-ui/Toast';
import PulsanteScanner from '../../../components/orizon-ui/PulsanteScanner';
import { FORMATI_SCANNER } from '../../../lib/gruBarcode';
import FormModal, { useFormSalva } from '../../../components/gru/FormModal';
import CampiDinamici, { bozzaDaAttributi, attributiDaBozza } from '../../../components/gru/CampiDinamici';
import { configScorta, formattaQta } from '../../../components/gru/scorta';
import {
  GruError, mapGruError, codiceValido, gruGeneraCodice, gruOpzioni, gruLista, gruCampiReparto,
  gruCreaArticolo, gruAggiornaArticolo, gruRegistraRettifica, gruRegistraCorrezione,
  nuovaChiaveIdempotenza, gruCrea, gruElimina,
} from '../../../lib/services/gruService';

const arrotonda3 = (n) => Math.round((Number(n) + Number.EPSILON) * 1000) / 1000;

const decimaliDi = (testo) => {
  const s = String(testo ?? '').replace(',', '.');
  const i = s.indexOf('.');
  return i === -1 ? 0 : s.length - i - 1;
};

const vuoto = (v) => v === undefined || v === null || String(v).trim() === '';

/** Elementi di un dropdown: attivi + quello già selezionato anche se disattivato. */
const opzioniDropdown = (righe, corrente, etichetta) => righe
  .filter((r) => r.attivo || r.id === corrente)
  .map((r) => ({ value: r.id, label: `${etichetta(r)}${r.attivo ? '' : ' (disattivato)'}` }));

// Parte "anagrafica" dello stato (tutto tranne giacenza, nota del movimento e collegamenti gru)
const anagraficaDi = (s) => ({
  ean: s.ean, nome: s.nome, sap: s.sap, fornitore: s.fornitore, descrizione: s.descrizione,
  unitaId: s.unitaId, qtaMin: s.qtaMin, ubicazioneId: s.ubicazioneId, note: s.note,
  attivo: s.attivo, bozza: s.bozza,
});

/**
 * Nuovo articolo / modifica articolo.
 *
 * Creazione: giacenza 0 (poi si propone il carico iniziale); si può associare subito a una gru.
 * Modifica: in alto la giacenza (QtyStepper); in basso "Salva nello storico" e la nota.
 *   - Salva nello storico ATTIVO: la variazione diventa un movimento "Rettifica" e la nota è obbligatoria.
 *   - Salva nello storico DISATTIVATO: correzione diretta (movimento "correzione"): non compare nello
 *     Storico e la nota non serve.
 * La giacenza non si scrive mai direttamente: passa sempre da un movimento.
 */
const FormArticolo = ({ record, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const toast = useToast();

  const [elenchi, setElenchi] = useState({ reparti: [], unita: [], ubicazioni: [], magazzini: [], gru: [] });
  const [caricamento, setCaricamento] = useState(true);

  const [ean, setEan] = useState(record?.ean ?? '');
  const [eanSbloccato, setEanSbloccato] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [nome, setNome] = useState(record?.nome ?? '');
  const [sap, setSap] = useState(record?.codice_sap ?? '');
  const [fornitore, setFornitore] = useState(record?.codice_fornitore ?? '');
  const [descrizione, setDescrizione] = useState(record?.descrizione ?? '');
  const [repartoId, setRepartoId] = useState(record?.reparto_id ?? '');
  const [unitaId, setUnitaId] = useState(record?.unita_id ?? '');
  const [qtaMin, setQtaMin] = useState(record ? String(record.qta_min ?? 0) : '0');
  const [ubicazioneId, setUbicazioneId] = useState(record?.ubicazione_id ?? '');
  const [note, setNote] = useState(record?.note ?? '');
  const [attivo, setAttivo] = useState(record?.attivo ?? true);

  // gru e componenti a cui è associato l'articolo (opzionale; in modifica parte dalla prima gru associata)
  const [gruId, setGruId] = useState(record?.gru_ids?.[0] ?? '');
  const [componentiDisponibili, setComponentiDisponibili] = useState([]);
  const [componentiScelti, setComponentiScelti] = useState([]); // modifica: più componenti collegati
  const [componenteId, setComponenteId] = useState(''); // creazione: il componente da cui precompilare e a cui collegare
  const [collegamenti, setCollegamenti] = useState({}); // componente_id -> id della riga di compatibilità
  const [caricamentoComponenti, setCaricamentoComponenti] = useState(false);

  const [campi, setCampi] = useState([]);
  const [caricamentoCampi, setCaricamentoCampi] = useState(false);
  const [bozza, setBozza] = useState({});

  // giacenza (solo modifica)
  const qtaOriginale = Number(record?.qta ?? 0);
  const [qtaNuova, setQtaNuova] = useState(qtaOriginale);
  const [salvaStorico, setSalvaStorico] = useState(true);
  const [nota, setNota] = useState('');
  const [chiave] = useState(() => nuovaChiaveIdempotenza());

  // flag di caricamento iniziale (servono solo per fissare lo snapshot in modifica)
  const [campiPronti, setCampiPronti] = useState(false);
  const [componentiPronti, setComponentiPronti] = useState(false);

  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();

  const stato = {
    ean, nome, sap, fornitore, descrizione, repartoId, unitaId, qtaMin, ubicazioneId,
    note, attivo, bozza, qtaNuova, salvaStorico, nota, gruId, componentiScelti, componenteId,
  };

  // Snapshot iniziale per rilevare le modifiche non salvate.
  // Creazione: al primo render. Modifica: appena caricati campi dinamici e componenti collegati.
  const baseRef = useRef(null);
  const anagraficaBaseRef = useRef(null);
  if (baseRef.current === null && !record) {
    baseRef.current = JSON.stringify(stato);
    anagraficaBaseRef.current = JSON.stringify(anagraficaDi(stato));
  }
  const sporco = baseRef.current !== null && JSON.stringify(stato) !== baseRef.current;

  useEffect(() => {
    if (modifica && campiPronti && componentiPronti && baseRef.current === null) {
      baseRef.current = JSON.stringify(stato);
      anagraficaBaseRef.current = JSON.stringify(anagraficaDi(stato));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campiPronti, componentiPronti]);

  // elenchi per i dropdown (da tabelle, non da codice)
  useEffect(() => {
    let annullato = false;
    Promise.all([
      gruOpzioni('reparti'), gruOpzioni('unita'), gruOpzioni('ubicazioni'), gruOpzioni('magazzini'), gruOpzioni('gru'),
    ])
      .then(([reparti, unita, ubicazioni, magazzini, gru]) => {
        if (annullato) return;
        setElenchi({ reparti, unita, ubicazioni, magazzini, gru });
        setCaricamento(false);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapGruError(e).message);
        setCaricamento(false);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // campi dinamici del reparto scelto
  useEffect(() => {
    if (!repartoId) {
      setCampi([]);
      setCampiPronti(true);
      return undefined;
    }
    let annullato = false;
    setCaricamentoCampi(true);
    gruCampiReparto(repartoId)
      .then((righe) => {
        if (annullato) return;
        setCampi(righe);
        const iniziale = repartoId === record?.reparto_id ? bozzaDaAttributi(righe, record?.attributi) : {};
        setBozza(iniziale);
        setCaricamentoCampi(false);
        setCampiPronti(true);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapGruError(e).message);
        setCaricamentoCampi(false);
        setCampiPronti(true);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repartoId]);

  // Componenti della gru scelta che appartengono al reparto scelto.
  // In modifica pre-seleziona quelli già collegati all'articolo.
  useEffect(() => {
    setComponenteId('');
    if (!gruId || !repartoId) {
      setComponentiDisponibili([]);
      setComponentiScelti([]);
      setCollegamenti({});
      setComponentiPronti(true);
      return undefined;
    }
    let annullato = false;
    setCaricamentoComponenti(true);
    Promise.all([
      gruOpzioni('componenti', { gru_id: gruId, reparto_id: repartoId }),
      modifica
        ? gruLista('compatibilita', { perPagina: 200, filtri: { articolo_id: record.id, gru_id: gruId } })
        : Promise.resolve({ righe: [] }),
    ])
      .then(([componenti, link]) => {
        if (annullato) return;
        const disponibili = componenti.filter((c) => c.attivo);
        const mappa = {};
        link.righe.forEach((l) => { mappa[l.componente_id] = l.id; });
        setComponentiDisponibili(disponibili);
        setCollegamenti(mappa);
        setComponentiScelti(disponibili.filter((c) => mappa[c.id]).map((c) => c.id));
        setCaricamentoComponenti(false);
        setComponentiPronti(true);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapGruError(e).message);
        setCaricamentoComponenti(false);
        setComponentiPronti(true);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gruId, repartoId]);

  // Creazione: scegliendo un componente, i dati tecnici si compilano con quelli già salvati nel componente.
  // I campi restano modificabili; se il componente non ha un valore, il campo non viene toccato.
  useEffect(() => {
    if (modifica || !componenteId || caricamentoCampi || campi.length === 0) return;
    const componente = componentiDisponibili.find((c) => c.id === componenteId);
    if (!componente) return;
    const daComponente = bozzaDaAttributi(campi, componente.attributi);
    if (Object.keys(daComponente).length === 0) return;
    setBozza((precedente) => ({ ...precedente, ...daComponente }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componenteId, campi, caricamentoCampi]);

  const unitaCorrente = elenchi.unita.find((u) => u.id === unitaId);
  const decimali = Number(unitaCorrente?.decimali ?? record?.unita_decimali ?? 0);
  const unitaLabel = unitaCorrente?.label ?? record?.unita_label ?? '';

  const opzioniReparti = useMemo(
    () => opzioniDropdown(elenchi.reparti, record?.reparto_id, (r) => r.label),
    [elenchi.reparti, record],
  );
  const opzioniGru = useMemo(
    () => opzioniDropdown(elenchi.gru, null, (g) => `${g.codice} — ${g.nome}`),
    [elenchi.gru],
  );
  const opzioniUnita = useMemo(
    () => opzioniDropdown(elenchi.unita, record?.unita_id, (u) => u.label),
    [elenchi.unita, record],
  );
  const opzioniUbicazioni = useMemo(() => {
    const nomeMagazzino = (id) => elenchi.magazzini.find((m) => m.id === id)?.label ?? '';
    return opzioniDropdown(elenchi.ubicazioni, record?.ubicazione_id, (u) => `${nomeMagazzino(u.magazzino_id)} · ${u.codice}`);
  }, [elenchi.ubicazioni, elenchi.magazzini, record]);

  const genera = async () => {
    setGenerando(true);
    try {
      const nuovo = await gruGeneraCodice();
      setEan(nuovo);
      pulisci('ean');
      toast.success('Codice generato.');
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setGenerando(false);
    }
  };

  // Codice letto con lo scanner (Code 128 / QR) o digitato nello scanner
  const dopoScansione = (letto) => {
    const pulito = String(letto).trim().replace(/[^A-Za-z0-9-]/g, '').slice(0, 24);
    setEan(pulito);
    pulisci('ean');
    if (!codiceValido(pulito)) {
      toast.warn('Il codice letto non è valido (4-24 caratteri: lettere, numeri, trattino): controllalo o correggilo a mano.');
    }
  };

  const delta = arrotonda3(qtaNuova - qtaOriginale);
  const statoNuovo = qtaNuova <= 0 ? 'esaurito' : qtaNuova < Number(String(qtaMin).replace(',', '.') || 0) ? 'sotto_scorta' : 'ok';
  const cfgScorta = configScorta(statoNuovo);

  const invia = async () => {
    const nuovi = {};

    if (!modifica || eanSbloccato) {
      if (vuoto(ean)) nuovi.ean = 'Obbligatorio';
      else if (!codiceValido(ean.trim())) nuovi.ean = 'Codice non valido: 4-24 caratteri, solo lettere, numeri e trattino';
    }
    if (vuoto(nome)) nuovi.nome = 'Obbligatorio';
    if (vuoto(repartoId)) nuovi.reparto_id = 'Obbligatorio';
    if (vuoto(unitaId)) nuovi.unita_id = 'Obbligatorio';

    const min = vuoto(qtaMin) ? 0 : Number(String(qtaMin).replace(',', '.'));
    if (!Number.isFinite(min) || min < 0) nuovi.qta_min = 'Numero non valido';
    else if (decimaliDi(qtaMin) > decimali) {
      nuovi.qta_min = decimali === 0 ? 'Solo quantità intere' : `Massimo ${decimali} decimali`;
    }

    const { attributi, errori: erroriAttributi } = attributiDaBozza(campi, bozza, record?.attributi);
    Object.assign(nuovi, erroriAttributi);

    // la nota serve solo se la variazione viene registrata nello storico
    if (modifica && delta !== 0 && salvaStorico && vuoto(nota)) {
      nuovi.nota = 'Nota obbligatoria per registrare la variazione';
    }

    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    // ─── Creazione ───────────────────────────────────────────────────────────
    if (!modifica) {
      await salva(
        async () => {
          const creato = await gruCreaArticolo({
            ean,
            nome: nome.trim(),
            repartoId,
            unitaId,
            qtaMin: min,
            attributi,
            codiceSap: vuoto(sap) ? null : sap.trim(),
            codiceFornitore: vuoto(fornitore) ? null : fornitore.trim(),
            descrizione: vuoto(descrizione) ? null : descrizione.trim(),
            ubicazioneId: vuoto(ubicazioneId) ? null : ubicazioneId,
            note: vuoto(note) ? null : note.trim(),
          });

          // associazione opzionale ai componenti scelti (una per volta: un errore non annulla l'articolo)
          const falliti = [];
          for (const idComponente of (componenteId ? [componenteId] : [])) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await gruCrea('compatibilita', {
                articolo_id: creato.id, componente_id: idComponente, quantita: 1, primario: false,
              });
            } catch (e) {
              falliti.push(componenteId);
            }
          }
          return { id: creato.id, falliti: falliti.length };
        },
        `Articolo "${nome.trim()}" creato.`,
        (r) => {
          if (r.falliti > 0) {
            toast.warn(`Articolo creato, ma ${r.falliti} associazione/i ai componenti non riuscite: completale dal dettaglio.`);
          }
          onSaved({ creato: true, id: r.id });
        },
      );
      return;
    }

    // ─── Modifica ────────────────────────────────────────────────────────────
    const anagraficaCambiata = JSON.stringify(anagraficaDi(stato)) !== anagraficaBaseRef.current;
    const daAggiungere = componentiScelti.filter((id) => !collegamenti[id]);
    const daRimuovere = componentiDisponibili
      .filter((c) => collegamenti[c.id] && !componentiScelti.includes(c.id))
      .map((c) => collegamenti[c.id]);
    const collegamentiCambiati = daAggiungere.length > 0 || daRimuovere.length > 0;

    await salva(
      async () => {
        if (anagraficaCambiata) {
          await gruAggiornaArticolo(record.id, {
            ...(eanSbloccato && ean !== record.ean ? { ean } : {}),
            nome: nome.trim(),
            codice_sap: vuoto(sap) ? null : sap.trim(),
            codice_fornitore: vuoto(fornitore) ? null : fornitore.trim(),
            descrizione: vuoto(descrizione) ? null : descrizione.trim(),
            unita_id: unitaId,
            qta_min: min,
            ubicazione_id: vuoto(ubicazioneId) ? null : ubicazioneId,
            note: vuoto(note) ? null : note.trim(),
            attributi,
            attivo,
          });
        }

        // associazioni alle gru: una per volta, gli errori non bloccano il resto
        let linkFalliti = 0;
        for (const componenteId of daAggiungere) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await gruCrea('compatibilita', {
              articolo_id: record.id, componente_id: componenteId, quantita: 1, primario: false,
            });
          } catch (e) {
            linkFalliti += 1;
          }
        }
        for (const idLink of daRimuovere) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await gruElimina('compatibilita', idLink);
          } catch (e) {
            linkFalliti += 1;
          }
        }

        if (delta !== 0) {
          try {
            if (salvaStorico) {
              await gruRegistraRettifica({ chiave, articoloId: record.id, delta, motivo: nota.trim() });
            } else {
              await gruRegistraCorrezione({ chiave, articoloId: record.id, delta });
            }
          } catch (e) {
            const err = mapGruError(e);
            const salvatoAltro = anagraficaCambiata || collegamentiCambiati;
            throw new GruError({
              code: err.code,
              message: `${salvatoAltro ? 'Dati salvati, ma la' : 'La'} giacenza non è stata aggiornata: ${err.message}`,
              campo: null,
              detail: err.detail,
              originale: err.originale,
            });
          }
        }
        return { delta, salvaStorico, anagraficaCambiata, collegamentiCambiati, linkFalliti };
      },
      (r) => {
        const parti = [];
        if (r.anagraficaCambiata) parti.push('dati aggiornati');
        if (r.collegamentiCambiati) parti.push('gru aggiornate');
        if (r.delta !== 0) {
          const segno = r.delta > 0 ? '+' : '';
          parti.push(`giacenza ${r.salvaStorico ? 'rettificata' : 'corretta'} (${segno}${formattaQta(r.delta, decimali)})`);
        }
        if (parti.length === 0) return 'Nessuna modifica da salvare.';
        const testo = parti.join(', ');
        return `Articolo: ${testo}.`;
      },
      (r) => {
        if (r?.linkFalliti > 0) {
          toast.warn(`${r.linkFalliti} associazione/i alle gru non riuscite: controllale dal dettaglio.`);
        }
        onSaved({ creato: false, id: record.id });
      },
    );
  };

  const classeBottone = 'flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50 whitespace-nowrap';

  return (
    <FormModal
      titolo={modifica ? 'Modifica articolo' : 'Nuovo articolo'}
      etichetta="Magazzino GRU"
      larghezza="xl"
      sporco={sporco}
      salvando={salvando}
      salvaLabel={modifica ? 'Salva modifiche' : 'Crea articolo'}
      onClose={onClose}
      onSubmit={invia}
    >
      {/* ── In alto (solo modifica): giacenza ── */}
      {modifica && (
        <FormSection titolo="Giacenza" icona="Box">
          <div className="flex flex-col gap-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Disponibile</span>
            <QtyStepper
              value={qtaNuova}
              onChange={(v) => { setQtaNuova(v); pulisci('nota'); }}
              originale={qtaOriginale}
              decimali={decimali}
              colore={cfgScorta.colore}
              unitaLabel={unitaLabel}
              disabilitato={salvando}
            />
          </div>
        </FormSection>
      )}

      <FormSection titolo="Identificazione" icona="Fingerprint">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
          <div className="flex-1 min-w-0">
            <TextField
              label="Codice" name="ean" value={ean}
              onChange={(v) => { setEan(v.replace(/[^A-Za-z0-9-]/g, '').slice(0, 24)); pulisci('ean'); }}
              obbligatorio errore={errori.ean} mono maxLength={24}
              disabilitato={modifica && !eanSbloccato}
              placeholder="es. GRU-0000123"
              suggerimento={modifica && !eanSbloccato ? 'Codice bloccato: sbloccalo solo per correggere un errore.' : '4-24 caratteri: lettere, numeri, trattino.'}
            />
          </div>
          {modifica && !eanSbloccato ? (
            <button
              type="button"
              onClick={() => setEanSbloccato(true)}
              className={`${classeBottone} sm:mt-5 bg-amber-500/10 border-amber-500/30 text-amber-500 hover:bg-amber-500/20`}
            >
              <Icon name="Lock" size={12} />
              Sblocca codice
            </button>
          ) : (
            <div className="flex flex-wrap gap-2 sm:mt-5">
              <PulsanteScanner onRilevato={dopoScansione} titolo="Scansiona codice" formati={FORMATI_SCANNER} />
              <button
                type="button"
                onClick={genera}
                disabled={generando}
                className={`${classeBottone} bg-primary/10 border-primary/30 text-primary hover:bg-primary/20`}
              >
                <Icon name={generando ? 'Loader2' : 'Barcode'} size={14} className={generando ? 'animate-spin' : ''} />
                Genera codice
              </button>
            </div>
          )}
        </div>

        <TextField
          label="Nome articolo" name="nome" value={nome}
          onChange={(v) => { setNome(v); pulisci('nome'); }}
          obbligatorio errore={errori.nome} autoFocus={!modifica}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextField label="Codice SAP" name="codice_sap" value={sap} onChange={setSap} mono errore={errori.codice_sap} />
          <TextField label="Codice fornitore" name="codice_fornitore" value={fornitore} onChange={setFornitore} mono errore={errori.codice_fornitore} />
        </div>
        <TextareaField label="Descrizione" name="descrizione" value={descrizione} onChange={setDescrizione} righe={2} errore={errori.descrizione} />
      </FormSection>

      <FormSection titolo="Classificazione e magazzino" icona="Boxes">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <SelectField
            label="Reparto" name="reparto_id" value={repartoId}
            onChange={(v) => { setRepartoId(v); pulisci('reparto_id'); }}
            opzioni={opzioniReparti} obbligatorio errore={errori.reparto_id}
            disabilitato={caricamento || modifica}
            suggerimento={modifica ? 'Il reparto non si cambia dopo la creazione.' : undefined}
          />
          <SelectField
            label="Unità di misura" name="unita_id" value={unitaId}
            onChange={(v) => { setUnitaId(v); pulisci('unita_id'); pulisci('qta_min'); }}
            opzioni={opzioniUnita} obbligatorio errore={errori.unita_id} disabilitato={caricamento}
          />
          <NumberField
            label="Scorta minima" name="qta_min" value={qtaMin}
            onChange={(v) => { setQtaMin(v); pulisci('qta_min'); }}
            suffisso={unitaLabel} errore={errori.qta_min} min={0}
          />
          <SelectField
            label="Ubicazione" name="ubicazione_id" value={ubicazioneId}
            onChange={setUbicazioneId} opzioni={opzioniUbicazioni} segnaposto="— Nessuna —"
            errore={errori.ubicazione_id} disabilitato={caricamento}
          />
        </div>

        <div className="flex flex-col gap-4 pt-2 border-t border-border/20">
          {modifica && (
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50">
              Gru attuali: <span className="text-primary/80 font-mono normal-case tracking-normal">{record.gru_codici || 'nessuna'}</span>
            </p>
          )}

          <SelectField
            label="Gru" name="gru_id" value={gruId}
            onChange={(v) => setGruId(v)}
            opzioni={opzioniGru} segnaposto="— Nessuna —" disabilitato={caricamento}
            suggerimento={modifica
              ? 'Scegli una gru per aggiungere o togliere i componenti a cui l\'articolo è associato.'
              : 'Facoltativo: collega subito l\'articolo ai componenti di questa gru. Si può fare anche dopo, dal dettaglio.'}
          />

          {gruId && !repartoId && (
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">
              Seleziona anche il reparto per vedere i componenti della gru.
            </p>
          )}

          {gruId && repartoId && (
            caricamentoComponenti ? (
              <div className="flex items-center gap-3 text-muted-foreground/50">
                <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                <span className="text-[10px] font-black uppercase tracking-widest">Caricamento componenti...</span>
              </div>
            ) : componentiDisponibili.length === 0 ? (
              <InfoBox tono="warn">
                Questa gru non ha componenti attivi per il reparto scelto. Creali in Configurazione → Componenti.
              </InfoBox>
            ) : modifica ? (
              <div className="flex flex-col gap-2">
                <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/60 px-1">
                  Componenti compatibili
                </span>
                {componentiDisponibili.map((c) => (
                  <ToggleField
                    key={c.id}
                    label={`${c.reparto_label}${c.posizione ? ` ${c.posizione}` : ''}`}
                    sublabel={c.etichetta || undefined}
                    checked={componentiScelti.includes(c.id)}
                    onChange={(v) => setComponentiScelti((prec) => (v ? [...prec, c.id] : prec.filter((x) => x !== c.id)))}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <SelectField
                  label="Componente compatibile" name="componente_id" value={componenteId}
                  onChange={(v) => setComponenteId(v)}
                  opzioni={componentiDisponibili.map((c) => ({
                    value: c.id,
                    label: `${c.reparto_label}${c.posizione ? ` ${c.posizione}` : ''}${c.etichetta ? ` — ${c.etichetta}` : ''}`,
                  }))}
                  segnaposto="— Nessun componente —"
                  suggerimento="Facoltativo. Scegliendolo, i dati tecnici si compilano con quelli del componente e l'articolo viene collegato dopo il salvataggio."
                />
                {componenteId && (
                  <InfoBox>
                    I dati tecnici sono stati compilati dal componente: puoi modificarli. Dopo il salvataggio l'articolo sarà collegato a questo componente (quantità 1).
                  </InfoBox>
                )}
              </div>
            )
          )}
        </div>
      </FormSection>

      <FormSection titolo="Dati tecnici del reparto" icona="Cpu">
        {!repartoId ? (
          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">
            Seleziona un reparto per compilare i dati tecnici.
          </p>
        ) : caricamentoCampi ? (
          <div className="flex items-center gap-3 text-muted-foreground/50">
            <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <span className="text-[10px] font-black uppercase tracking-widest">Caricamento campi...</span>
          </div>
        ) : (
          <CampiDinamici
            campi={campi}
            bozza={bozza}
            onChange={(b) => {
              Object.keys(b).filter((k) => b[k] !== bozza[k]).forEach((k) => pulisci(`attributi.${k}`));
              setBozza(b);
            }}
            errori={errori}
          />
        )}
      </FormSection>

      <TextareaField label="Note sull'articolo" name="note" value={note} onChange={setNote} righe={2} errore={errori.note} />

      {modifica && (
        <ToggleField
          label="Attivo"
          sublabel={attivo ? 'Selezionabile negli interventi' : 'Disattivato: non selezionabile, lo storico resta'}
          checked={attivo}
          onChange={setAttivo}
        />
      )}

      {/* ── In basso (solo modifica): storico e nota del movimento ── */}
      {modifica ? (
        <FormSection titolo="Storico e nota" icona="History">
          <ToggleField
            label="Salva nello storico"
            sublabel={salvaStorico
              ? 'Attivo: la variazione di giacenza viene registrata nello storico e la nota è obbligatoria'
              : 'Disattivato: correzione diretta, nessuna riga nello storico e nessuna nota'}
            checked={salvaStorico}
            onChange={(v) => { setSalvaStorico(v); pulisci('nota'); }}
            disabilitato={salvando}
          />

          {delta !== 0 && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/5 border border-primary/15">
              <Icon name="ArrowRightLeft" size={12} className="text-primary/60 shrink-0" />
              <span className="text-[10px] font-black uppercase tracking-widest text-primary/60">
                {salvaStorico ? 'Movimento:' : 'Correzione:'}&nbsp;
                <span className={delta > 0 ? 'text-emerald-500' : 'text-red-500'}>
                  {salvaStorico ? 'Rettifica' : 'Senza storico'} {delta > 0 ? '+' : ''}{formattaQta(delta, decimali)} {unitaLabel}
                </span>
              </span>
            </div>
          )}

          {salvaStorico ? (
            <TextareaField
              label="Nota del movimento" name="nota" value={nota}
              onChange={(v) => { setNota(v); pulisci('nota'); }}
              placeholder={delta !== 0
                ? 'Inserisci il motivo della variazione (es. Differenza inventario, merce danneggiata...)'
                : 'Serve solo se modifichi la giacenza'}
              obbligatorio={delta !== 0} errore={errori.nota} righe={3}
            />
          ) : (
            <InfoBox tono="warn">
              Correzione diretta: se modifichi la giacenza il valore cambia senza comparire nello Storico.
            </InfoBox>
          )}
        </FormSection>
      ) : (
        <InfoBox>
          L'articolo viene creato con giacenza 0. Dopo il salvataggio potrai registrare subito il carico iniziale.
        </InfoBox>
      )}
    </FormModal>
  );
};

export default FormArticolo;
